import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
// import axios, { AxiosError } from "axios";
import { AxiosError } from "axios";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import Container from "../../components/Container";
import PageTitle from "../../components/PageTitle";
import StepNav from "../../components/StepNav";
import Button from "../../components/Button";
import terms from "../../assets/pdf/terms-of-service.pdf";
import userGuide from "../../assets/pdf/user-guide.pdf";

import { getPrefectureName, getPrefectureCode } from "../../utils/helpers";
import { getProducts, postOrder } from "../../utils/api";
import type { OrdersRequest, ProductsResponse } from "../../utils/api";
import styles from "./index.module.scss";

const isFullWidthKatakanaTrimmed = (s: string) => /^[\u30A1-\u30FA\u30FD-\u30FF\u3000\u30FB\u30FC]+$/.test(s.trim());

export default function OrderPage() {
    const [stepCount, setStepCount] = useState(1);

    const [ordererFamilyName, setOrdererFamilyName] = useState("");
    const [ordererGivenName, setOrdererGivenName] = useState("");
    const [ordererFamilyNameKana, setOrdererFamilyNameKana] = useState("");
    const [ordererGivenNameKana, setOrdererGivenNameKana] = useState("");
    const [ordererTel, setOrdererTel] = useState({
        1: "",
        2: "",
        3: "",
    });
    const [ordererPostalCode3, setOrdererPostalCode3] = useState("");
    const [ordererPostalCode4, setOrdererPostalCode4] = useState("");
    const [ordererPrefectureCode, setOrdererPrefectureCode] = useState("");
    const [ordererCity, setOrdererCity] = useState("");
    const [ordererStreetAddress, setOrdererStreetAddress] = useState("");
    const [ordererBuildingName, setOrdererBuildingName] = useState("");

    const ordererInputRefs = useRef<HTMLInputElement[]>([]);
    const ordererSelectRefs = useRef<HTMLSelectElement[]>([]);
    const [ordererError, setOrdererError] = useState("");
    const [ordererErrorNumber, setOrdererErrorNumber] = useState(0);
    const [prefectureCheckFlag, setPrefectureCheckFlag] = useState(true);
    const [payload, setPayload] = useState<OrdersRequest>();

    const [consent, setConsent] = useState(false);

    const [paymentMethod, setPaymentMethod] = useState("CREDITCARD_WAP");
    const [remarks, setRemarks] = useState("");

    const navigate = useNavigate();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSameAsOrderer = true;

    const prefectureCodes = Array.from({ length: 47 }, (_, i) => String(1 + i).padStart(2, "0"));
    const { product_id } = useParams();
    // const [orderId, setOrderId] = useState("");
    // const [error, setError] = useState("");
    const [selectedProduct, setSelectedProduct] = useState<ProductsResponse | null>(null);

    const { data: productsDataStove } = useSWR("productsDataStove", () => getProducts("stove"), {
        revalidateOnFocus: false, // ページフォーカス時の再フェッチを無効化
        onError: (err: AxiosError) => {
            console.error("Error fetching products stove data:", err);
            // ここでエラー表示などを行いたい場合は追加
        },
    });
    const { data: productsDataHeater } = useSWR("productsDataHeater", () => getProducts("heater"), {
        revalidateOnFocus: false, // ページフォーカス時の再フェッチを無効化
        onError: (err: AxiosError) => {
            console.error("Error fetching products heater data:", err);
            // ここでエラー表示などを行いたい場合は追加
        },
    });

    // product_idに基づいて商品を検索
    useEffect(() => {
        if (product_id && (productsDataStove || productsDataHeater)) {
            const allProducts = [...(productsDataStove || []), ...(productsDataHeater || [])];
            const product = allProducts.find((p) => p.id === product_id);
            setSelectedProduct(product || null);
        }
        const localProductId = localStorage.getItem("localProductId");
        if (!product_id && localProductId) {
            const allProducts = [...(productsDataStove || []), ...(productsDataHeater || [])];
            const product = allProducts.find((p) => p.id === localProductId);
            setSelectedProduct(product || null);
        }
    }, [product_id, productsDataStove, productsDataHeater]);

    const orderTelCodeHandle = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newCode = e.target.value.replace(/[^\d]/g, "");
        if ((index === 1 && newCode.length < 4) || (index !== 1 && newCode.length < 5)) {
            setOrdererTel((prevState) => ({ ...prevState, [index]: newCode }));
        } else {
            return;
        }
        const check = [];
        check[1] = ordererTel[1];
        check[2] = ordererTel[2];
        check[3] = ordererTel[3];
        check[index] = newCode;

        if (newCode.length === 4 || (index === 1 && newCode.length === 3)) {
            const nextIndex = index < 4 ? index + 1 : 1; // 最後のフィールドなら最初に戻る
            if (ordererInputRefs.current[nextIndex]) {
                ordererInputRefs.current[nextIndex].focus();
            }
        }
    };
    const handleOrdererFullInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fullNumber = event.target.value.replace(/[^0-9]/g, ""); // 数字以外を除去
        if (fullNumber.length >= 10) {
            const part1 = fullNumber.slice(0, 3); // 最初の3桁 (市外局番)
            const part3 = fullNumber.slice(-4); // 最後の4桁 (末尾)
            const part2 = fullNumber.slice(3, -4); // 真ん中部分 (残り)
            setOrdererTel({ 1: part1, 2: part2, 3: part3 });
        }
    };
    const handelOrderPostal3 = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newCode = e.target.value.replace(/[^\d]/g, "");
        setOrdererPostalCode3(newCode);
    };
    const handelOrderPostal4 = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newCode = e.target.value.replace(/[^\d]/g, "");
        setOrdererPostalCode4(newCode);
    };

    // 郵便番号から住所を取得する関数
    const fetchOrdererAddress = async (zipcode: string) => {
        try {
            const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zipcode}`);
            const data = await response.json();

            if (data.results) {
                const result = data.results[0];
                setOrdererPrefectureCode(getPrefectureCode(result.address1) ?? "");
                if (getPrefectureCode(result.address1) === "40" || getPrefectureCode(result.address1) === "42" || getPrefectureCode(result.address1) === "43") {
                    setPrefectureCheckFlag(true);
                } else {
                    setPrefectureCheckFlag(false);
                }
                setOrdererCity(`${result.address2}${result.address3}`);
                // setError(""); // エラークリア
            } else {
                // setError(
                //     "住所が見つかりませんでした。正しい郵便番号を入力してください。"
                // );
            }
        } catch (err) {
            console.log(err);
            // setError("データ取得中にエラーが発生しました。");
        }
    };

    const handleValidate = async () => {
        const ordererPostal = `${ordererPostalCode3}-${ordererPostalCode4}`;
        const ordererPhoneNumber = `${ordererTel[1]}-${ordererTel[2]}-${ordererTel[3]}`;
        // 各種送信フラグ（ペイロード操作用）

        const ordererBuildingNameFlg = ordererBuildingName !== "";
        // const shipBuildingNameFlg = shipBuildingName !== "";

        const payloadData: Partial<OrdersRequest> = {};
        // 姓名・郵便番号・住所が揃わないと送信できないように、一か所でも入力が在るとエラー

        if (!ordererFamilyName) {
            setOrdererError("お名前（姓）を入力してください。");
            setOrdererErrorNumber(1);
            return;
        }
        if (!ordererGivenName) {
            setOrdererError("お名前（名）を入力してください。");
            setOrdererErrorNumber(2);
            return;
        }
        if (!ordererFamilyNameKana) {
            setOrdererError("フリガナ（姓）を入力してください。");
            setOrdererErrorNumber(3);
            return;
        }
        if (!isFullWidthKatakanaTrimmed(ordererFamilyNameKana)) {
            setOrdererError("フリガナ（姓）を全角カタカナで入力してください。");
            setOrdererErrorNumber(3);
            return;
        }
        if (!ordererGivenNameKana) {
            setOrdererError("フリガナ（名）を入力してください。");
            setOrdererErrorNumber(4);
            return;
        }
        if (!isFullWidthKatakanaTrimmed(ordererGivenNameKana)) {
            setOrdererError("フリガナ（名）を全角カタカナで入力してください。");
            setOrdererErrorNumber(4);
            return;
        }

        if (ordererPhoneNumber.length < 13) {
            setOrdererError("携帯番号を入力してください。");
            setOrdererErrorNumber(5);
            return;
        }

        if (ordererTel[1] !== "090" && ordererTel[1] !== "080" && ordererTel[1] !== "070" && ordererTel[1] !== "060") {
            setOrdererError("携帯番号を正しく入力してください。");
            setOrdererErrorNumber(5);
            return;
        }

        if (ordererPostal.length !== 8) {
            setOrdererError("郵便番号を正しく入力してください");
            setOrdererErrorNumber(6);
            return;
        }
        if (!ordererPrefectureCode) {
            setOrdererError("都道府県を選択してください。");
            setOrdererErrorNumber(7);
            return;
        }
        if (!ordererCity) {
            setOrdererError("市区町村・町名を入力してください。");
            setOrdererErrorNumber(8);
            return;
        }
        if (!ordererStreetAddress) {
            setOrdererError("丁目・番地・号を入力してください。");
            setOrdererErrorNumber(9);
            return;
        }

        setOrdererError("");
        setOrdererErrorNumber(0);

        payloadData.orderer_given_name = ordererGivenName;
        payloadData.orderer_family_name = ordererFamilyName;
        payloadData.orderer_given_kana = ordererGivenNameKana;
        payloadData.orderer_family_kana = ordererFamilyNameKana;

        payloadData.orderer_phone = ordererPhoneNumber;

        payloadData.orderer_postal_code = ordererPostal;
        payloadData.orderer_pref_code = ordererPrefectureCode;
        payloadData.orderer_city = ordererCity;
        payloadData.orderer_address1 = ordererStreetAddress;

        if (ordererBuildingNameFlg) {
            payloadData.orderer_address2 = ordererBuildingName;
        }

        if (isSameAsOrderer) {
            payloadData.ship_family_name = ordererFamilyName;
            payloadData.ship_given_name = ordererGivenName;
            payloadData.ship_family_kana = ordererFamilyNameKana;
            payloadData.ship_given_kana = ordererGivenNameKana;
            payloadData.ship_phone = ordererPhoneNumber;
            payloadData.ship_postal_code = ordererPostal;
            payloadData.ship_pref_code = ordererPrefectureCode;
            payloadData.ship_city = ordererCity;
            payloadData.ship_address1 = ordererStreetAddress;
            if (ordererBuildingNameFlg) {
                payloadData.ship_address2 = ordererBuildingName;
            }
        }
        if (!selectedProduct?.id) {
            return;
        }
        payloadData.products = [
            {
                product_id: selectedProduct.id,
                quantity: 1,
            },
        ];
        // "8076c0821d1690aa4985d1e5a0aec664fa573bb8725410b281c52e8aaa2e433b"
        payloadData.payment_method = paymentMethod as "PAYPAY_WAP" | "CREDITCARD_WAP";
        if (remarks.trim()) {
            payloadData.remarks = remarks.trim(); // 備考
        }
        setPayload(payloadData as OrdersRequest);
        setStepCount(2);
        window.scrollTo(0, 0);
    };
    const handleSubmit = async () => {
        setIsSubmitting(true);
        localStorage.setItem("localProductId", selectedProduct?.id || "");
        if (payload) {
            try {
                const response = await postOrder(payload, navigate);

                if (response?.success && "order_id" in response) {
                    if (payload.payment_method === "PAYPAY_WAP") {
                        setStepCount(3);
                    }
                    window.location.href = `/user/paymul/redirect/${response.order_token}/`;
                    // setOrderId(response.order_id);
                } else {
                    console.log("476response", response);
                }
            } catch (error) {
                console.error("APIエラー:", error);
            }
        }
    };
    const handleBack = () => {
        localStorage.removeItem("localProductId");
        if (selectedProduct?.product_type === "stove") {
            navigate("/pr_tableconro");
        } else {
            navigate("/pr_fh");
        }
    };
    useEffect(() => {
        const newZipcode = `${ordererPostalCode3}${ordererPostalCode4}`;
        // 郵便番号が7桁に満たない場合はリクエストを送信しない
        if (newZipcode.length === 7) {
            fetchOrdererAddress(newZipcode);
        }
    }, [ordererPostalCode3, ordererPostalCode4]);

    useEffect(() => {
        // 半角モードに変換
        const inputElements = document.querySelectorAll(".numericInput");
        inputElements.forEach((inputElement) => {
            if (inputElement) {
                inputElement.addEventListener("focus", () => {
                    // フォーカス時に数字入力モードに設定
                    inputElement.setAttribute("inputmode", "numeric");
                });

                inputElement.addEventListener("blur", () => {
                    // フォーカスが外れたら入力モードをリセット
                    inputElement.removeAttribute("inputmode");
                });
            }
        });
    }, []);
    return (
        <>
            <Helmet>
                <title>西部ガス：商品注文画面</title>
                <meta name="description" content="西部ガスグループ公式オンラインショップの注文画面です。" />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <div className={styles.orderPage} data-page="order">
                {/* エラーチェック */}
                {/* <Button variant="white" className="noto-sans-jp-bold" onClick={() => {
                    throw new Error("test");
                }} style={{position:"fixed", top:0, left:0, zIndex:1000}}>test</Button> */}
                <AnimatePresence mode="wait">
                    {stepCount === 1 && (
                        <motion.section
                            key="step1"
                            className={styles.orderPage__inputSection}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}>
                            <PageTitle text="ご注文情報のご入力" />
                            <Container>
                                <StepNav step={1} />
                                <div className={styles.orderPage__box}>
                                    <h3 className="noto-sans-jp-bold" style={{ fontSize: "3.2rem" }}>
                                        ご注文商品
                                    </h3>

                                    <div className={styles.orderPage__orderItem__info}>
                                        <div className={styles.orderPage__orderItem__info__image}>
                                            {selectedProduct?.eyecatch_image_url ? (
                                                <img
                                                    src={
                                                        selectedProduct.eyecatch_image_url.startsWith("http")
                                                            ? selectedProduct.eyecatch_image_url
                                                            : selectedProduct.eyecatch_image_url.startsWith("/")
                                                            ? `${window.location.origin}${selectedProduct.eyecatch_image_url}`
                                                            : `${window.location.origin}/${selectedProduct.eyecatch_image_url}`
                                                    }
                                                    alt={selectedProduct.name || "商品画像"}
                                                    onError={(e) => {
                                                        console.error("画像の読み込みに失敗しました:", selectedProduct.eyecatch_image_url);
                                                        // フォールバック画像を設定するか、エラー表示
                                                        (e.target as HTMLImageElement).style.display = "none";
                                                    }}
                                                    onLoad={() => {
                                                        console.log("画像の読み込みが完了しました:", selectedProduct.eyecatch_image_url);
                                                    }}
                                                />
                                            ) : (
                                                <div className={styles.orderPage__orderItem__info__image__placeholder}>{selectedProduct ? "画像を読み込み中..." : "商品情報を読み込み中..."}</div>
                                            )}
                                        </div>
                                        <div className={styles.orderPage__orderItem__info__textSection}>
                                            <div className={`${styles.orderPage__orderItem__info__textSection__header} noto-sans-jp-semibold`}>
                                                {selectedProduct ? `${selectedProduct.name} ${selectedProduct.model}` : "商品を読み込み中..."}
                                            </div>
                                            <div className={styles.orderPage__orderItem__info__textSection__footer}>
                                                <div className={`${styles.orderPage__orderItem__info__textSection__price} noto-sans-myanmar-semibold`}>
                                                    &yen;
                                                    {selectedProduct ? selectedProduct.sales_price?.toLocaleString() : "0"}
                                                    （税込）
                                                </div>
                                                <p onClick={handleBack} className={`${styles.orderPage__orderItem__info__textSection__backButton} noto-sans-jp-regular`}>
                                                    削除
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p className={styles.annotation}>(!)本商品は都市ガス(12A・13A)用商品です。都市ガス以外ではご使用いただけませんのでご注意ください。</p>

                                {/* 注文者情報 */}
                                <div className={`${styles.orderPage__inputForm} ${styles.orderPage__box} `}>
                                    <h3 className="noto-sans-jp-bold" style={{ paddingBottom: ".5rem" }}>
                                        注文者情報
                                    </h3>
                                    <div className={`${styles.orderPage__inputForm__nameFlex}`}>
                                        <div>
                                            <p className={`${styles.orderPage__inputForm__label} required`}>お名前（姓）</p>
                                            <input
                                                type="text"
                                                value={ordererFamilyName}
                                                onChange={(e) => setOrdererFamilyName(e.target.value)}
                                                className={`${ordererErrorNumber === 1 ? styles.orderPage__inputForm__inputError : ""}`}
                                                autoComplete="family-name"
                                            />
                                        </div>
                                        <div>
                                            <p className={`${styles.orderPage__inputForm__label} required`}>お名前（名）</p>
                                            <input
                                                type="text"
                                                value={ordererGivenName}
                                                onChange={(e) => setOrdererGivenName(e.target.value)}
                                                className={`${ordererErrorNumber === 2 ? styles.orderPage__inputForm__inputError : ""}`}
                                                autoComplete="given-name"
                                            />
                                        </div>
                                    </div>
                                    <div className={`${styles.orderPage__inputForm__nameFlex}`}>
                                        <div>
                                            <p className={`${styles.orderPage__inputForm__label} required`}>フリガナ（姓）</p>
                                            <input
                                                type="text"
                                                value={ordererFamilyNameKana}
                                                onChange={(e) => setOrdererFamilyNameKana(e.target.value)}
                                                className={`${ordererErrorNumber === 3 ? styles.orderPage__inputForm__inputError : ""}`}
                                            />
                                        </div>
                                        <div>
                                            <p className={`${styles.orderPage__inputForm__label} required`}>フリガナ（名）</p>
                                            <input
                                                type="text"
                                                value={ordererGivenNameKana}
                                                onChange={(e) => setOrdererGivenNameKana(e.target.value)}
                                                className={`${ordererErrorNumber === 4 ? styles.orderPage__inputForm__inputError : ""}`}
                                            />
                                        </div>
                                    </div>

                                    <div className={``}>
                                        <p className={`${styles.orderPage__inputForm__label} required`}>携帯番号</p>
                                        <div className={`${styles.orderPage__inputForm__phoneFlex}`}>
                                            <input
                                                onInput={handleOrdererFullInput}
                                                type="tel"
                                                inputMode="numeric"
                                                maxLength={11}
                                                onChange={(e) => orderTelCodeHandle(e, 1)}
                                                value={ordererTel[1]}
                                                className={` inter-bold numericInput ${ordererErrorNumber === 5 ? styles.orderPage__inputForm__inputError : ""}`}
                                                ref={(el) => {
                                                    ordererInputRefs.current[1] = el!;
                                                }}
                                                autoComplete="tel-national"
                                            />
                                            -
                                            <input
                                                onInput={handleOrdererFullInput}
                                                type="tel"
                                                inputMode="numeric"
                                                maxLength={11}
                                                onChange={(e) => orderTelCodeHandle(e, 2)}
                                                value={ordererTel[2]}
                                                className={`inter-bold numericInput ${ordererErrorNumber === 5 ? styles.orderPage__inputForm__inputError : ""}`}
                                                ref={(el) => {
                                                    ordererInputRefs.current[2] = el!;
                                                }}
                                                autoComplete="tel-national"
                                            />
                                            -
                                            <input
                                                onInput={handleOrdererFullInput}
                                                type="tel"
                                                inputMode="numeric"
                                                maxLength={11}
                                                onChange={(e) => orderTelCodeHandle(e, 3)}
                                                value={ordererTel[3]}
                                                className={`inter-bold numericInput ${ordererErrorNumber === 5 ? styles.orderPage__inputForm__inputError : ""}`}
                                                ref={(el) => {
                                                    ordererInputRefs.current[3] = el!;
                                                }}
                                                autoComplete="tel-national"
                                            />
                                        </div>
                                    </div>
                                    <h3 className="noto-sans-jp-bold" style={{ paddingBottom: ".5rem", marginTop: "4rem" }}>
                                        商品の設置先住所
                                    </h3>
                                    <p className={`${styles.linkBlue} noto-sans-jp-bold`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="svg-icon" style={{width: "1.25em", height: "1.25em",verticalAlign: "middle",fill: "currentColor",overflow: "hidden"}} viewBox="0 0 1024 1024" version="1.1"><path d="M464 784.352c0 26.51 21.49 48 48 48s48-21.49 48-48-21.49-48-48-48-48 21.49-48 48z"/><path d="M512 960C264.96 960 64 759.04 64 512S264.96 64 512 64s448 200.96 448 448-200.96 448-448 448z m0-831.713c-211.584 0-383.713 172.129-383.713 383.713 0 211.552 172.129 383.713 383.713 383.713 211.552 0 383.713-172.16 383.713-383.713 0-211.584-172.161-383.713-383.713-383.713z"/><path d="M512 673.695c-17.665 0-32-14.336-32-31.999v-54.112c0-52.353 39.999-92.352 75.327-127.648 25.887-25.92 52.672-52.672 52.672-74.016 0-53.344-43.072-96.736-95.999-96.736-53.823 0-96 41.536-96 94.56 0 17.664-14.335 31.999-32 31.999s-32-14.336-32-32c0-87.423 71.774-158.559 160-158.559S672 297.28 672 385.92c0 47.904-36.32 84.191-71.424 119.296-27.84 27.776-56.575 56.512-56.575 82.335v54.112c0 17.665-14.336 32.032-32.001 32.032z"/></svg>
                                        サービス対応エリアは
                                        <a href="https://hd.saibugas.co.jp/business/gas/#serviceArea" target="_blank" rel="noopener noreferrer">
                                            こちら
                                        </a>
                                    </p>
                                    <div className={``}>
                                        <p className={`${styles.orderPage__inputForm__label} required`}>郵便番号</p>
                                        <div className={`${styles.orderPage__inputForm__postalcodeFlex}`}>
                                            <span className={`${styles.orderPage__inputForm__postalcodeSpan} noto-sans-jp-semibold`}>〒</span>
                                            <input
                                                type="text"
                                                value={ordererPostalCode3}
                                                onChange={(e) => handelOrderPostal3(e)}
                                                className={`numericInput inter-bold ${ordererErrorNumber === 6 ? styles.orderPage__inputForm__inputError : ""}`}
                                                maxLength={3} // 3桁に制限
                                                inputMode="numeric"
                                                ref={(el) => {
                                                    ordererInputRefs.current[4] = el!;
                                                }}
                                            />
                                            -
                                            <input
                                                type="text"
                                                value={ordererPostalCode4}
                                                onChange={(e) => handelOrderPostal4(e)}
                                                className={`numericInput inter-bold ${ordererErrorNumber === 6 ? styles.orderPage__inputForm__inputError : ""}`}
                                                maxLength={4} // 4桁に制限
                                                inputMode="numeric"
                                                ref={(el) => {
                                                    ordererInputRefs.current[5] = el!;
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className={``}>
                                        <p className={`${styles.orderPage__inputForm__label} required`}>都道府県（自動入力）</p>
                                        <div className="select-container">
                                            <select
                                                value={ordererPrefectureCode}
                                                onChange={(e) => setOrdererPrefectureCode(e.target.value)}
                                                ref={(el) => {
                                                    ordererSelectRefs.current[0] = el!;
                                                }}
                                                disabled
                                                className={`${prefectureCheckFlag ? "" : "redborder"} noto-sans-jp-semibold ${
                                                    ordererErrorNumber === 7 ? styles.orderPage__inputForm__inputError : ""
                                                }`}>
                                                <option value=""></option>
                                                {prefectureCodes.map((item) => (
                                                    <option key={item} value={item}>
                                                        {getPrefectureName(item)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* 福岡長崎熊本はご注文いただけません。 */}
                                        {ordererPrefectureCode && !prefectureCheckFlag && (
                                            <p className={`noto-sans-jp-bold ${styles.orderPage__inputForm__prefectureCheckFlag}`}>※西部ガスの都市ガスのサービスエリア外の方はご注文いただけません。</p>
                                        )}
                                    </div>
                                    <div className={``}>
                                        <p className={`${styles.orderPage__inputForm__label} required`}>市区町村・町名</p>
                                        <input
                                            type="text"
                                            value={ordererCity}
                                            onChange={(e) => setOrdererCity(e.target.value)}
                                            className={`${ordererErrorNumber === 8 ? styles.orderPage__inputForm__inputError : ""}`}
                                        />
                                    </div>

                                    <div className={``}>
                                        <p className={`${styles.orderPage__inputForm__label} required`}>丁目・番地・号</p>
                                        <input
                                            type="text"
                                            value={ordererStreetAddress}
                                            onChange={(e) => setOrdererStreetAddress(e.target.value)}
                                            className={`${ordererErrorNumber === 9 ? styles.orderPage__inputForm__inputError : ""}`}
                                        />
                                    </div>

                                    <div className={``}>
                                        <p className={`${styles.orderPage__inputForm__label} `}>建物名（任意）</p>
                                        <input type="text" value={ordererBuildingName} onChange={(e) => setOrdererBuildingName(e.target.value)} />
                                    </div>

                                    <div className={``}>
                                        <p className={`${styles.orderPage__inputForm__label} `}>備考欄（任意）</p>
                                        <textarea
                                            value={remarks}
                                            onChange={(e) => {
                                                setRemarks(e.target.value);
                                                // 自動リサイズ
                                                e.target.style.height = "auto";
                                                e.target.style.height = e.target.scrollHeight + "px";
                                            }}
                                            placeholder="ご要望等ございましたらご入力ください"
                                            className={styles.orderPage__inputForm__textarea}
                                            rows={3}
                                            style={{
                                                resize: "none",
                                                overflow: "hidden",
                                                minHeight: "6rem",
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* お支払い方法 */}
                                <div className={`${styles.orderPage__inputForm} ${styles.orderPage__box} `}>
                                    <h3
                                        className="noto-sans-jp-bold"
                                        style={{
                                            paddingBottom: ".5rem",
                                            marginBottom: "1.6rem",
                                        }}>
                                        お支払い方法
                                    </h3>
                                    <div>
                                        <label htmlFor="paymentMethod1" className={`${styles.orderPage__inputForm__radioLabel} noto-sans-jp-bold`}>
                                            <input type="radio" id="paymentMethod1" checked={paymentMethod === "CREDITCARD_WAP"} onChange={() => setPaymentMethod("CREDITCARD_WAP")} />
                                            <span>クレジットカード</span>
                                        </label>
                                        <p
                                            className="noto-sans-jp-bold"
                                            style={{
                                                color: "#000",
                                                fontSize: "2.2rem",
                                            }}>
                                            ご利用可能なクレジットブランド
                                        </p>
                                        <p
                                            className="noto-sans-jp-medium"
                                            style={{
                                                fontSize: "2.2rem",
                                                lineHeight: "1",
                                            }}>
                                            VISA、MasterCard、JCB、AMEX、Diners Club、DISCOVER
                                        </p>
                                        <label htmlFor="paymentMethod2" className={`${styles.orderPage__inputForm__radioLabel} noto-sans-jp-bold`} style={{ marginTop: "2rem" }}>
                                            <input type="radio" id="paymentMethod2" checked={paymentMethod === "PAYPAY_WAP"} onChange={() => setPaymentMethod("PAYPAY_WAP")} />
                                            <span>PayPay</span>
                                        </label>
                                    </div>
                                </div>
                                {/* 配送情報 */}
                                <div className={`${styles.orderPage__inputForm} ${styles.orderPage__radiusBox} `}>
                                    <p style={{ fontSize: "1.8rem" }} className="noto-sans-jp-medium">
                                        商品は西部ガスリビングメイトより発送させていただきます。
                                    </p>
                                    <p className={`${styles.orderPage__shippingCharges} noto-sans-jp-bold`}>送料：無料</p>
                                    <p style={{ fontSize: "1.6rem" }} className="noto-sans-jp-medium">
                                        ※お客さまの設置状況により追加工事が発生する場合がございます。追加工事費は、もよりの西部ガスグループ販売店（リビングメイト） とのご契約になります。
                                    </p>
                                </div>
                                <label
                                    htmlFor="consent"
                                    style={{
                                        fontSize: "1.8rem",
                                        textAlign: "center",
                                    }}
                                    className={`${styles.orderPage__inputForm__consent} noto-sans-jp-medium`}>
                                    <input id="consent" type="checkbox" className="noto-sans-jp-bold" style={{ margin: "0 auto" }} checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                                    <span>
                                        <a href={terms} target="_blank">
                                            利用規約
                                        </a>
                                        、
                                        <a href={userGuide} target="_blank">
                                            ご利用ガイド
                                        </a>
                                        および
                                        <a href="https://www.saibugas.co.jp/policy/kojin_tori.htm" target="_blank" rel="noopener noreferrer">
                                            個人情報の取扱いについて
                                        </a>
                                        に同意する
                                    </span>
                                </label>
                                {!selectedProduct && (
                                    <p
                                        className={`noto-sans-jp-bold `}
                                        style={{
                                            color: "#FF0600",
                                            textAlign: "center",
                                            marginBottom: "2rem",
                                            fontSize: "1.8rem",
                                        }}>
                                        商品が選択されていません。
                                    </p>
                                )}
                                {ordererError && (
                                    <p
                                        className={`noto-sans-jp-bold `}
                                        style={{
                                            color: "#FF0600",
                                            textAlign: "center",
                                            marginBottom: "2rem",
                                            fontSize: "1.8rem",
                                        }}>
                                        注文者情報の{ordererError}
                                    </p>
                                )}

                                <div className={styles.orderPage__buttonWrapper}>
                                    <Button variant="blue" onClick={handleValidate} disabled={!consent || !prefectureCheckFlag}>
                                        次へ
                                    </Button>
                                </div>
                            </Container>
                        </motion.section>
                    )}
                    {stepCount === 2 && (
                        <motion.section
                            key="step2"
                            className={styles.orderPage__confirmSection}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}>
                            <PageTitle text="ご注文内容のご確認" />
                            <Container>
                                <StepNav step={2} />
                                <p
                                    style={{
                                        fontSize: "1.8rem",
                                        textAlign: "center",
                                        marginBottom: "2rem",
                                    }}
                                    className="noto-sans-jp-medium">
                                    以下の内容で問題なければ
                                    <br />
                                    「注文を確定する」ボタンをクリックしてください。
                                </p>
                                {/* 注文者情報 */}
                                <div className={`${styles.orderPage__confirmFrom} ${styles.orderPage__box} `} data-section="confirm">
                                    <h3 className="noto-sans-jp-bold" style={{ paddingBottom: "2.5rem" }}>
                                        注文者情報
                                    </h3>
                                    <div className={`${styles.orderPage__table}`}>
                                        <div className={`${styles.orderPage__table__row}`}>
                                            <div className={`${styles.orderPage__table__th}`}>名前</div>
                                            <div className={`${styles.orderPage__table__td}`}>
                                                {ordererFamilyName} {ordererGivenName}
                                            </div>
                                        </div>
                                        <div className={`${styles.orderPage__table__row}`}>
                                            <div className={`${styles.orderPage__table__th}`}>フリガナ</div>
                                            <div className={`${styles.orderPage__table__td}`}>
                                                {ordererFamilyNameKana} {ordererGivenNameKana}
                                            </div>
                                        </div>
                                        <div className={`${styles.orderPage__table__row}`}>
                                            <div className={`${styles.orderPage__table__th}`}>携帯番号</div>
                                            <div className={`${styles.orderPage__table__td} inter-bold`}>
                                                {ordererTel[1]} {ordererTel[2]} {ordererTel[3]}
                                            </div>
                                        </div>
                                        <div className={`${styles.orderPage__table__row}`}>
                                            <div className={`${styles.orderPage__table__th}`}>商品の設置先住所</div>
                                            <div className={`${styles.orderPage__table__td}`}>
                                                <p className="inter-bold">
                                                    〒 {ordererPostalCode3} - {ordererPostalCode4}
                                                </p>
                                                <p>
                                                    {ordererPrefectureCode ? getPrefectureName(ordererPrefectureCode) : ""} {ordererCity} {ordererStreetAddress} {ordererBuildingName}
                                                </p>
                                            </div>
                                        </div>
                                        <div className={`${styles.orderPage__table__row}`}>
                                            <div className={`${styles.orderPage__table__th}`}>備考欄</div>
                                            <div className={`${styles.orderPage__table__td}`}>
                                                <p>{remarks}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* お支払い方法 */}
                                <div className={`${styles.orderPage__confirmFrom} ${styles.orderPage__box} `}>
                                    <h3 className="noto-sans-jp-bold" style={{ paddingBottom: "2.5rem" }}>
                                        お支払い方法
                                    </h3>
                                    <div>
                                        <p className="noto-sans-jp-bold" style={{ fontSize: "2.4rem" }}>
                                            {paymentMethod === "CREDITCARD_WAP" ? <>クレジットカード</> : <>PayPay</>}
                                        </p>
                                    </div>
                                </div>

                                {/* ご請求金額 */}
                                <div className={`${styles.orderPage__confirmFrom} ${styles.orderPage__radiusBox} `} style={{ paddingBottom: "1rem" }}>
                                    <h3 className="noto-sans-jp-bold" style={{ paddingBottom: "2.5rem" }}>
                                        ご請求金額
                                    </h3>
                                    <div className={`${styles.orderPage__billingTable}`}>
                                        <div className={`${styles.orderPage__billingTable__row}`}>
                                            <div className={`${styles.orderPage__billingTable__th}`} style={{ textAlign: "left" }}>
                                                {selectedProduct?.name}
                                                <br />
                                                {selectedProduct?.model}
                                            </div>
                                            <div className={`${styles.orderPage__billingTable__td} noto-sans-myanmar-semibold`}>
                                                &yen;
                                                {selectedProduct?.sales_price?.toLocaleString()}
                                            </div>
                                        </div>
                                        <div className={`${styles.orderPage__billingTable__row}`}>
                                            <div className={`${styles.orderPage__billingTable__th}`}>送料</div>
                                            <div className={`${styles.orderPage__billingTable__td} noto-sans-myanmar-semibold`}>&yen;{0}</div>
                                        </div>
                                        <div className={`${styles.orderPage__billingTable__row}`}>
                                            <div className={`${styles.orderPage__billingTable__th}`}>消費税率</div>
                                            <div className={`${styles.orderPage__billingTable__td} noto-sans-myanmar-semibold`}>10%</div>
                                        </div>
                                        <div className={`${styles.orderPage__billingTable__row}`}>
                                            <div className={`${styles.orderPage__billingTable__th}`} style={{ fontSize: "2.5rem" }}>
                                                ご請求金額（税込）
                                            </div>
                                            <div
                                                className={`${styles.orderPage__billingTable__td} noto-sans-myanmar-semibold`}
                                                style={{
                                                    borderBottom: "none",
                                                    fontSize: "2.8rem",
                                                }}>
                                                &yen;
                                                {selectedProduct?.sales_price?.toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p
                                    style={{
                                        fontSize: "2.5rem",
                                        textAlign: "center",
                                        lineHeight: "1.8",
                                    }}
                                    className="noto-sans-jp-medium">
                                    次の画面でお支払い情報をご入力ください
                                    <br />
                                    ※外部サイトに遷移します
                                </p>
                                <div className={styles.orderPage__buttonWrapper} style={{ margin: "4rem 0" }}>
                                    <Button
                                        variant="blue"
                                        onClick={() => {
                                            setStepCount(1);
                                        }}
                                        style={{ backgroundColor: "#ACACAC" }}
                                        disabled={isSubmitting}>
                                        修正する
                                    </Button>
                                    <Button
                                        variant="blue"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                        // className={isSubmitting ? "disabled" : ""}
                                    >
                                        注文を確定する
                                    </Button>
                                </div>
                                <p
                                    style={{
                                        fontSize: "2rem",
                                        textAlign: "center",
                                        lineHeight: "1.8",
                                    }}
                                    className="noto-sans-jp-medium">
                                    ブラウザの「戻る」ボタンは使用しないでください。
                                </p>
                            </Container>
                        </motion.section>
                    )}
                    {stepCount === 3 && (
                        <section className={styles.orderPage__errorSection}>
                            <Container>
                                <p
                                    style={{
                                        fontSize: "3.2rem",
                                        textAlign: "center",
                                    }}
                                    className="noto-sans-jp-bold">
                                    決済画面に遷移しました。
                                </p>

                                <p
                                    style={{
                                        fontSize: "2rem",
                                        lineHeight: "1.8",
                                        margin: "4rem 3rem 6rem",
                                        textAlign: "center",
                                    }}
                                    className="noto-sans-jp-medium">
                                    こちらの画面は閉じてください。
                                </p>
                            </Container>
                        </section>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
}
